import dayjs, { Dayjs } from "dayjs";
import { UnitTypes } from "../utils/unit";
import { createContext, useContext, useEffect, useMemo } from "react";
import useAuth from "../auth/useAuth";
import { I18nProvider as AriaI18nProvider } from "@react-aria/i18n";
import localizedFormat from "dayjs/plugin/localizedFormat";
import "dayjs/locale/pt";
import "dayjs/locale/en";

dayjs.extend(localizedFormat);

type I18NContext = {
    formatDateMMDDYYYY: (
        date: string | Date | Dayjs | undefined | null,
    ) => string;
    formatUnit: (
        imperial: keyof (typeof UnitTypes)["imperial"],
        metric: keyof (typeof UnitTypes)["metric"],
    ) => string;
    isImperial: boolean;
};

const I18NContext = createContext<I18NContext | null>(null);

const I18NProvider = ({ children }: { children: React.ReactNode }) => {
    const { user } = useAuth();

    useEffect(() => {
        if (user) {
            dayjs.locale(user.language_preference ?? "en");
        }
    }, [user]);

    const formatDateMMDDYYYY = (
        date: string | Date | Dayjs | undefined | null,
    ) => {
        if (!user || user?.date_format == "month_day_year") {
            return dayjs(date).format("MM/DD/YYYY");
        }
        if (user.date_format == "day_month_year") {
            return dayjs(date).format("DD/MM/YYYY");
        }
        if (user.date_format == "year_month_day") {
            return dayjs(date).format("YYYY/MM/DD");
        }
        return dayjs(date).format("MM/DD/YYYY");
    };

    const formatUnit = (
        imperial: keyof (typeof UnitTypes)["imperial"],
        metric: keyof (typeof UnitTypes)["metric"],
    ) => {
        const unit = user?.unit_preference ?? "imperial";
        return unit === "imperial"
            ? UnitTypes.imperial[imperial]
            : UnitTypes.metric[metric];
    };

    const isImperial = useMemo(
        () => user?.unit_preference === "imperial",
        [user?.unit_preference],
    );

    const ariaLocale = (user?.language_preference ?? "en")
        .toLowerCase()
        .startsWith("pt")
        ? "pt-BR"
        : "en-US";

    return (
        <AriaI18nProvider locale={ariaLocale}>
            <I18NContext.Provider
                value={{
                    formatDateMMDDYYYY,
                    formatUnit,
                    isImperial,
                }}
            >
                {children}
            </I18NContext.Provider>
        </AriaI18nProvider>
    );
};

const useI18n = () => {
    const context = useContext(I18NContext);
    if (!context) {
        throw new Error("useI18n must be used within an I18NProvider");
    }
    return context;
};

// eslint-disable-next-line react-refresh/only-export-components
export { I18NProvider, useI18n };
